/**
 * @architecture-header 1
 * @layer module
 * @domain live
 * @responsibility Build the fixed-field hand-off brief a teacher reads instead of the whole chat.
 * @state-owner none; callers pass the seat and session records in
 * @allowed-imports shared view types and demo constants
 * @forbidden React; network; store implementations
 * @public-api live intervention page
 * @side-effects none
 * @tests modules/live/model/handoff.test.ts
 */

import { HINTS, INITIAL_THREAD, QUESTIONS, TRIED } from '../../../shared/lib/demo-data';
import type { SeatRecord } from '../../../shared/lib/model';

/**
 * Whether a line is something that happened or something inferred from it. Anything about how a
 * student feels, why they did it, or whether they cheated is a reading, never a record, and is
 * labelled so the teacher weighs it accordingly.
 */
export type Certainty = 'fact' | 'likely' | 'uncertain';

export const CERTAINTY_LABEL: Readonly<Record<Certainty, string>> = {
  fact: '紀錄',
  likely: '推論',
  uncertain: '不確定',
};

export interface EvidenceItem {
  readonly at: string;
  readonly from: 'stu' | 'ai' | 'sys';
  readonly text: string;
}

export interface BriefField {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly certainty: Certainty;
  /** Two to four supporting lines, so a claim can be checked without opening the transcript. */
  readonly evidence: readonly EvidenceItem[];
}

export interface HandoffBrief {
  /** Which slice of the session this describes, so an old brief cannot read as the present. */
  readonly window: string;
  readonly version: number;
  readonly fields: readonly BriefField[];
}

const MAX_EVIDENCE = 4;

function studentLines(): readonly EvidenceItem[] {
  return INITIAL_THREAD.filter((message) => message.from === 'stu').map((message) => ({
    at: message.at ?? '',
    from: 'stu' as const,
    text: message.text,
  }));
}

export function buildHandoff(seat: SeatRecord, questionIndex = 1, version = 1): HandoffBrief {
  const question = QUESTIONS[questionIndex] ?? QUESTIONS[0];
  const said = studentLines();

  const fields: BriefField[] = [
    {
      id: 'goal',
      label: '任務目標',
      value: `${question.concept}：${question.text}`,
      certainty: 'fact',
      evidence: [{ at: '', from: 'sys', text: `正解 ${question.answer}` }],
    },
    {
      id: 'progress',
      label: '目前進度',
      value: `第 ${questionIndex + 1} 題，停在同一步 ${seat.stuckMinutes ?? seat.lastActiveMinutes} 分鐘`,
      certainty: 'fact',
      evidence: [
        { at: '', from: 'sys', text: `最後動作在 ${seat.lastActiveMinutes} 分鐘前` },
        { at: '', from: 'sys', text: `作答進度 ${seat.progress}` },
      ],
    },
    {
      id: 'reasoning',
      label: '學生的推理／誤解',
      value: '把項移到等號另一邊時沒有改變符號，因此把兩個 x 項相加。',
      certainty: 'likely',
      evidence: said.slice(0, MAX_EVIDENCE),
    },
    {
      id: 'tried',
      label: 'AI 已嘗試',
      value: `${TRIED.length} 種說法都沒有接住`,
      certainty: 'fact',
      evidence: TRIED.map((text) => ({ at: '', from: 'ai' as const, text })),
    },
    {
      id: 'response',
      label: '學生反應',
      value: '重述同一個算式，沒有往下一步走；可能已經有點放棄。',
      /** Motivation and mood are readings of behaviour, not observations of it. */
      certainty: 'uncertain',
      evidence: said.slice(-2),
    },
    {
      id: 'escalation',
      label: '升級原因',
      value: seat.reason ?? '連續兩次提示後仍保留同一個錯誤',
      certainty: 'fact',
      evidence: HINTS.map((text) => ({ at: '', from: 'ai' as const, text })),
    },
    {
      id: 'next',
      label: '建議下一步',
      value: '請他把 -3 移到右邊後唸出符號，讓他自己說出變號那一步。',
      certainty: 'likely',
      evidence: [{ at: '', from: 'sys', text: `已試過的說法：${TRIED.join('、')}` }],
    },
  ];

  return {
    window: `本節課至今 · 最後 ${seat.lastActiveMinutes} 分鐘內無新動作`,
    version,
    fields,
  };
}

/**
 * What the teacher did, kept apart from the brief above it. Mixing the two would let a later
 * reader mistake the teacher's own line for something the assistant observed.
 */
export function buildHandoffNote(student: string, message: string, at: string): string {
  return `${at} 老師介入：對 ${student} 說「${message}」`;
}
